import {FC, useEffect, useState} from 'react';
import {ReactComponent as ImportIcon} from '@icons/import.svg';
import {ReactComponent as SearchIcon} from '@icons/search-normal.svg';
import {BODY1, defaultPage, defaultPageSize, SUBTITLE1} from '@/utils/constants';
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import Pagination from '@/shared/components/Pagination';
import Input from '@/shared/components/Inputs/Input';
import {ReportsModalTablesStyle} from '../UnpaidItemsModal/ReportsModalTables.style';
import Button from '@/shared/components/Button';
import {ReactComponent as ArrowUpDownIcon} from '@icons/arrow-up-down.svg';
import {ReactComponent as TestImage} from '@/assets/images/testImage.svg';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {OrganizationReportsService} from '@/utils/services/organization-reports.service';
import {downloadFile, getErrorMessage} from '@/utils/helpers';
import {REPORTS_GET_FILE} from '@/utils/enums/organization-reports.enum';
import {toast} from 'react-toastify';
import {getMaxBidsSelector, getReportLoadingSelector, getReportMaxBids} from '@/store/organization-report';
import Loader from '@/shared/components/Loader';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import {IMaxBidsData} from '@/utils/types/organization-reports.interface';

const columnOptions: {label: string; key: keyof IMaxBidsData}[] = [
	{label: 'Bid #', key: 'id'},
	{label: 'First name', key: 'firstName'},
	{label: 'Last name', key: 'lastName'},
	{label: 'Table#', key: 'tableNumber'},
	{label: 'Table name', key: 'tableName'},
	{label: 'Item', key: 'itemName'},
	{label: 'Max bid', key: 'maxBid'},
];

const ValueofMaxBidsModal: FC = () => {
	const dispatch = useAppDispatch();

	const currentEvent = useAppSelector(getCurrentEventSelector);
	const maxBidsList = useAppSelector(getMaxBidsSelector);
	const isLoading = useAppSelector(getReportLoadingSelector);

	const [searchName, setSearchName] = useState('');
	const [page, setPage] = useState<number>(defaultPage);
	const [filterValues, setFilterValues] = useState(maxBidsList?.data || []);
	const [sortState, setSortState] = useState<{
		column: keyof IMaxBidsData | null;
		direction: 'asc' | 'desc';
	}>({
		column: null,
		direction: 'asc',
	});
	const [filteredAllValues, setFilteredAllValues] = useState<IMaxBidsData[]>([]);

	const getMaxBidsData = async (value?: string) => {
		if (!currentEvent) return;

		try {
			const reqBody = {
				eventId: currentEvent?.id,
				page,
				searchName,
				// searchName: value,
			};

			const {data} = await dispatch(getReportMaxBids(reqBody)).unwrap();

			setFilteredAllValues(data);
			setFilterValues(data.slice(0, defaultPageSize));
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const sortData = (column: keyof IMaxBidsData) => {
		const direction = sortState.column === column && sortState.direction === 'asc' ? 'desc' : 'asc';

		const sorted = [...filterValues].sort((a, b) => {
			const valA = a[column];
			const valB = b[column];

			if (typeof valA === 'number' && typeof valB === 'number') {
				return direction === 'asc' ? valA - valB : valB - valA;
			}

			return direction === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
		});

		setSortState({column, direction});
		setFilterValues(sorted);
	};

	const handleSearchChange = (name: string) => {
		setSearchName(name);
		setPage(1);
	};

	const handlePaginationChange = (step: number) => {
		setPage(step);

		const startIdx = (step - 1) * defaultPageSize;
		const endIdx = startIdx + defaultPageSize;
		setFilterValues(filteredAllValues.slice(startIdx, endIdx) || []);
	};

	const handleDownloadCSV = async () => {
		if (!currentEvent) return;

		try {
			const file = await OrganizationReportsService.getMaxBidsCsv(currentEvent?.id);
			downloadFile(file.data, REPORTS_GET_FILE.MAX_BIDS);
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const paginationText = maxBidsList?.totalCount
		? `Showing ${(page - 1) * defaultPageSize + 1} - ${Math.min(
				page * defaultPageSize,
				maxBidsList?.totalCount ?? 0
		  )} of ${maxBidsList?.totalCount} items`
		: 'Showing 0 items';

	useEffect(() => {
		if (!maxBidsList?.data) return;

		setFilteredAllValues(maxBidsList?.data);
		setFilterValues(maxBidsList?.data.slice(0, defaultPageSize));
	}, []);

	useEffect(() => {
		getMaxBidsData(searchName);
	}, [currentEvent, page, searchName]);

	return (
		<Box sx={ReportsModalTablesStyle.wrapper}>
			<Box sx={ReportsModalTablesStyle.filterRow}>
				<Typography variant={SUBTITLE1} sx={ReportsModalTablesStyle.title}>
					{paginationText}
				</Typography>

				<Box sx={ReportsModalTablesStyle.search}>
					<SearchIcon />
					<Input placeholder="Search" value={searchName} onChange={handleSearchChange} />
				</Box>
				<Box>
					<Button
						sx={ReportsModalTablesStyle.download}
						startIcon={<ImportIcon />}
						onClick={handleDownloadCSV}
						title="Download.csv"
						disabled={!maxBidsList?.data.length}
					/>
				</Box>
			</Box>

			{isLoading ? (
				<Loader parentClassName={ReportsModalTablesStyle.loader} size={50} />
			) : filterValues.length > 0 ? (
				<Box sx={ReportsModalTablesStyle.table}>
					<TableContainer>
						<Table sx={ReportsModalTablesStyle.tableStyle}>
							<TableHead>
								<TableRow sx={{height: '32px'}}>
									{columnOptions.map(({label, key}) => {
										const sortable =
											key === 'firstName' ||
											key === 'lastName' ||
											key === 'tableNumber' ||
											key === 'id' ||
											key === 'maxBid';
										return (
											<TableCell
												key={key}
												onClick={() => sortable && sortData(key)}
												sx={{cursor: sortable ? 'pointer' : 'default'}}
											>
												<Box
													sx={{display: 'flex', alignItems: 'center', gap: '4px'}}
													className={key === 'maxBid' ? 'lastItem' : ''}
												>
													{label}
													{sortable && <ArrowUpDownIcon />}
												</Box>
											</TableCell>
										);
									})}
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{filterValues.map((row, index) => (
									<TableRow key={index}>
										{columnOptions.map(({key}) => {
											if (key === 'itemName') {
												return (
													<TableCell key={key} className="itemMaxBid">
														<Box
															sx={{
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'flex-start',
																py: '8px',
																gap: '16px',
															}}
														>
															<img
																src={row.itemPhoto ?? TestImage}
																alt="auction-item"
																style={{width: '48px', height: '48px', borderRadius: '4px'}}
															/>
															<Box>
																<Typography sx={{fontSize: '14px', fontWeight: '700 !important'}}>
																	{row.itemName}
																</Typography>
																<Typography sx={{color: 'var(--neutral-300) !important'}}>
																	{row.itemCategory}
																</Typography>
															</Box>
														</Box>
													</TableCell>
												);
											}
											return (
												<TableCell key={key}>
													<Typography
														className={key === 'maxBid' ? 'lastItem' : ''}
														sx={{
															color:
																typeof row[key] === 'number' && key === 'maxBid'
																	? (row[key] as number) < 0
																		? 'var(--error-700) !important'
																		: 'var(--success-700) !important'
																	: 'var(--neutral-900) !important',
														}}
													>
														{typeof row[key] === 'number' && key === 'maxBid' ? `$${row[key]}` : row[key] || '-'}
													</Typography>
												</TableCell>
											);
										})}
										<TableCell>
											<ActionsMenu
												content={
													<Box sx={ReportsModalTablesStyle.menu}>
														<Typography sx={ReportsModalTablesStyle.menuContent} variant={BODY1}>
															Send payment link
														</Typography>
														<Typography sx={ReportsModalTablesStyle.menuContent} variant={BODY1}>
															Send receipt email
														</Typography>
													</Box>
												}
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>

					<Box sx={ReportsModalTablesStyle.paginationContainer}>
						<Pagination
							page={page}
							pageSize={defaultPageSize}
							total={maxBidsList?.totalCount || 0}
							onChange={handlePaginationChange}
						/>
					</Box>
				</Box>
			) : (
				<EmptyTableData />
			)}
		</Box>
	);
};

export default ValueofMaxBidsModal;
